import type { CommerceCalculateValueFrom } from '@/config/shared/commerce';
import { normalizeCalculateValueFrom } from '@/config/shared/commerce';
import type { PixelItem } from '@/events/conversion';

import type { CommerceState } from './CommerceState';
import type { ProductCatalog } from './ProductCatalog';
import type { OrderCalculator, ResolvedCommerceItem } from './OrderCalculator';

export type CommerceEventType =
  | 'ADD_TO_CART'
  | 'REMOVE_FROM_CART'
  | 'INITIATE_CHECKOUT'
  | 'PURCHASE'
  | 'VIEW_CONTENT'
  | 'ADD_PAYMENT_INFO';

export type ExtractedProductInput = {
  productId?: string;
  productName?: string;
  price?: number;
  quantity?: number;
  currency?: string;
  sku?: string;
};

export type CommerceResolverInput = {
  eventType: CommerceEventType;
  payload?: Record<string, unknown>;
  extractedProduct?: ExtractedProductInput;
  enrichmentSource?: import('@/config/shared/commerce').CommerceEnrichmentSource;
  items?: Array<{
    productId?: string;
    id?: string;
    sku?: string;
    name?: string;
    productName?: string;
    price?: number;
    quantity?: number;
    currency?: string;
    category?: string;
  }>;
};

export type CommerceResolverResult = {
  data: Record<string, unknown>;
  items?: ResolvedCommerceItem[];
  value?: number;
  currency?: string;
};

export type CommerceResolverOptions = {
  catalog: ProductCatalog;
  state: CommerceState;
  calculator: OrderCalculator;
  defaultCurrency?: string;
  calculateValueFrom?: CommerceCalculateValueFrom;
};

/**
 * Combina payload + extracted + state + catalog + defaults.
 * Não dispara eventos, não escreve em CommerceState, não conhece DOM/Transport.
 *
 * Prioridade de merge por campo:
 * manual payload > extractedProduct > commerce state > catalog > defaults
 */
export class CommerceResolver {
  private readonly catalog: ProductCatalog;

  private readonly state: CommerceState;

  private readonly calculator: OrderCalculator;

  private readonly defaultCurrency?: string;

  private readonly calculateValueFrom: ReturnType<
    typeof normalizeCalculateValueFrom
  >;

  constructor(options: CommerceResolverOptions) {
    this.catalog = options.catalog;
    this.state = options.state;
    this.calculator = options.calculator;
    this.defaultCurrency = trim(options.defaultCurrency);
    this.calculateValueFrom = normalizeCalculateValueFrom(
      options.calculateValueFrom
    );
  }

  resolve(input: CommerceResolverInput): CommerceResolverResult {
    const source = input.enrichmentSource;

    // source: 'event' — não completa via cart/catalog
    if (source === 'event') {
      return { data: { ...(input.payload ?? {}) } };
    }

    switch (input.eventType) {
      case 'ADD_TO_CART':
        return this.resolveAddToCart(input);
      case 'REMOVE_FROM_CART':
        return this.resolveRemoveFromCart(input);
      case 'INITIATE_CHECKOUT':
        return this.resolveCheckoutLike(input, false);
      case 'PURCHASE':
        return this.resolveCheckoutLike(input, true);
      case 'VIEW_CONTENT':
        return this.resolveViewContent(input);
      case 'ADD_PAYMENT_INFO':
        return this.resolveCheckoutLike(input, false);
      default:
        return { data: { ...(input.payload ?? {}) } };
    }
  }

  private resolveAddToCart(
    input: CommerceResolverInput
  ): CommerceResolverResult {
    const payload = input.payload ?? {};
    const extracted = input.extractedProduct ?? {};
    const source = input.enrichmentSource ?? 'event_or_cart';
    const useCart = source === 'cart' || source === 'event_or_cart';
    const useCatalog =
      source === 'catalog' || source === 'cart' || source === 'event_or_cart';

    let productId =
      asString(payload.priceId) ??
      asString(payload.productId) ??
      asString(extracted.productId) ??
      (useCatalog
        ? this.lookupIdBySku(asString(payload.sku) ?? asString(extracted.sku))
        : undefined);

    // Fallback: sem productId/sku no evento → item principal do cart
    const cartFallback =
      !productId && useCart ? this.resolvePrimaryCartItem() : null;
    if (!productId && cartFallback) {
      productId = cartFallback.productId;
    }

    const catalogProduct =
      useCatalog && productId
        ? this.catalog.getById(productId)
        : useCatalog
          ? this.lookupProductBySku(
              asString(payload.sku) ?? asString(extracted.sku)
            )
          : null;

    const resolvedId = productId ?? catalogProduct?.id;
    const stateItem =
      useCart && resolvedId
        ? (this.state.getItem(resolvedId) ?? cartFallback)
        : null;

    // source: 'catalog' sem productId/sku → não injeta cart
    if (source === 'catalog' && !resolvedId && !catalogProduct) {
      return { data: { ...payload } };
    }

    const productName =
      asString(payload.productName) ??
      asString(extracted.productName) ??
      catalogProduct?.name;

    const price =
      asNumber(payload.price) ??
      asNumber(extracted.price) ??
      (useCart ? asNumber(stateItem?.price) : undefined) ??
      catalogProduct?.price;

    const quantity =
      asNumber(payload.quantity) ??
      asNumber(extracted.quantity) ??
      (useCart ? asNumber(stateItem?.quantity) : undefined) ??
      catalogProduct?.quantity ??
      1;

    const currency =
      asString(payload.currency) ??
      asString(extracted.currency) ??
      (useCart ? asString(stateItem?.currency) : undefined) ??
      catalogProduct?.currency ??
      this.defaultCurrency;

    const identity = resolveCatalogItemIdentity(catalogProduct, {
      priceId: asString(payload.priceId),
      productId: resolvedId,
      offerId: asString(payload.offerId),
      id: asString(payload.id),
    });

    const data: Record<string, unknown> = {};
    if (identity.id) data.id = identity.id;
    if (identity.priceId) data.priceId = identity.priceId;
    if (identity.productId) data.productId = identity.productId;
    if (identity.offerId) data.offerId = identity.offerId;
    if (productName) data.productName = productName;
    if (price !== undefined) data.price = price;
    if (currency) data.currency = currency;
    if (quantity !== undefined) data.quantity = quantity;

    // Preserva campos extras do payload (sem sobrescrever resolvidos).
    for (const [key, value] of Object.entries(payload)) {
      if (value === undefined) continue;
      if (!(key in data)) data[key] = value;
    }

    return { data };
  }

  /**
   * Escolhe o item principal do cart runtime:
   * 1. único item → esse
   * 2. múltiplos → preferir catalog.type === 'main'
   * 3. senão → primeiro do cart
   * 4. cart vazio → null
   */
  private resolvePrimaryCartItem(): {
    productId: string;
    quantity?: number;
    price?: number;
    currency?: string;
  } | null {
    const items = this.state.getSelectedItems();
    if (items.length === 0) return null;
    if (items.length === 1) return items[0] ?? null;

    const main = items.find(item => {
      const product = this.catalog.getById(item.productId);
      return product?.type === 'main';
    });
    return main ?? items[0] ?? null;
  }

  private resolveRemoveFromCart(
    input: CommerceResolverInput
  ): CommerceResolverResult {
    const payload = input.payload ?? {};
    const extracted = input.extractedProduct ?? {};

    const productId =
      asString(payload.productId) ??
      asString(extracted.productId) ??
      this.lookupIdBySku(asString(payload.sku) ?? asString(extracted.sku));

    const catalogProduct = productId ? this.catalog.getById(productId) : null;

    const resolvedId = productId ?? catalogProduct?.id;
    const stateItem = resolvedId ? this.state.getItem(resolvedId) : null;

    const quantity =
      asNumber(payload.quantity) ??
      asNumber(extracted.quantity) ??
      asNumber(stateItem?.quantity) ??
      1;

    const data: Record<string, unknown> = {};
    if (resolvedId) data.productId = resolvedId;
    if (quantity !== undefined) data.quantity = quantity;

    for (const [key, value] of Object.entries(payload)) {
      if (value === undefined) continue;
      if (!(key in data)) data[key] = value;
    }

    return { data };
  }

  private resolveCheckoutLike(
    input: CommerceResolverInput,
    isPurchase: boolean
  ): CommerceResolverResult {
    const payload = { ...(input.payload ?? {}) };
    const manualValue = asNumber(payload.value);
    const manualCurrency = asString(payload.currency);
    const manualItems = this.readPayloadItems(input);
    const source = input.enrichmentSource;

    const resolvedItems = this.resolveItemsSource(manualItems, source);
    const pixelItems = resolvedItems.map(item => this.toPixelItem(item));

    const allowCalculatedValue =
      source !== 'catalog' && this.calculateValueFrom !== 'manual';

    const shouldCalculateValue =
      manualValue === undefined && allowCalculatedValue;

    const calc = this.calculator.calculate({
      items: resolvedItems,
      value: shouldCalculateValue ? undefined : manualValue,
      currency: manualCurrency,
      defaultCurrency: this.defaultCurrency,
    });

    const value =
      manualValue !== undefined
        ? manualValue
        : allowCalculatedValue
          ? calc.value
          : undefined;

    const currency = manualCurrency ?? calc.currency ?? this.defaultCurrency;

    const data: Record<string, unknown> = { ...payload };

    if (pixelItems.length > 0) {
      data.items = pixelItems;
    } else if (manualItems === null) {
      delete data.items;
    }

    if (value !== undefined) {
      data.value = value;
    } else {
      delete data.value;
    }

    if (currency) {
      data.currency = currency;
    }

    if (isPurchase) {
      // orderId e demais campos do payload já estão em data via spread.
    }

    return {
      data,
      items: calc.items,
      ...(value !== undefined ? { value } : {}),
      ...(currency ? { currency } : {}),
    };
  }

  private resolveViewContent(
    input: CommerceResolverInput
  ): CommerceResolverResult {
    const payload = { ...(input.payload ?? {}) };
    const source = input.enrichmentSource ?? 'event_or_cart';

    // Tenta completar productId/name/price a partir de payload + cart/catalog
    if (source === 'cart' || source === 'event_or_cart') {
      const atc = this.resolveAddToCart({
        ...input,
        eventType: 'ADD_TO_CART',
        enrichmentSource: source,
      });

      const checkout = this.resolveCheckoutLike(
        { ...input, eventType: 'INITIATE_CHECKOUT', enrichmentSource: source },
        false
      );

      const data: Record<string, unknown> = { ...payload };

      const contentId =
        asString(payload.contentId) ??
        asString(atc.data.productId as string | undefined) ??
        (Array.isArray(checkout.data.items) &&
        checkout.data.items[0] &&
        typeof (checkout.data.items[0] as { id?: unknown }).id === 'string'
          ? (checkout.data.items[0] as { id: string }).id
          : undefined);

      const contentName =
        asString(payload.contentName) ??
        asString(atc.data.productName as string | undefined) ??
        (Array.isArray(checkout.data.items) &&
        checkout.data.items[0] &&
        typeof (checkout.data.items[0] as { name?: unknown }).name === 'string'
          ? (checkout.data.items[0] as { name: string }).name
          : undefined);

      if (contentId) data.contentId = contentId;
      if (contentName) data.contentName = contentName;

      if (
        asNumber(payload.value) === undefined &&
        checkout.value !== undefined
      ) {
        data.value = checkout.value;
      }
      if (!asString(payload.currency) && checkout.currency) {
        data.currency = checkout.currency;
      }
      if (payload.items === undefined && Array.isArray(checkout.data.items)) {
        data.items = checkout.data.items;
      }

      return {
        data,
        ...(checkout.value !== undefined ? { value: checkout.value } : {}),
        ...(checkout.currency ? { currency: checkout.currency } : {}),
        items: checkout.items,
      };
    }

    if (source === 'catalog') {
      const atc = this.resolveAddToCart({
        ...input,
        eventType: 'ADD_TO_CART',
        enrichmentSource: 'catalog',
      });
      const data: Record<string, unknown> = { ...payload };
      if (atc.data.productId && !asString(payload.contentId)) {
        data.contentId = atc.data.productId;
      }
      if (atc.data.productName && !asString(payload.contentName)) {
        data.contentName = atc.data.productName;
      }
      if (
        atc.data.price !== undefined &&
        asNumber(payload.value) === undefined
      ) {
        data.value = atc.data.price;
      }
      if (atc.data.currency && !asString(payload.currency)) {
        data.currency = atc.data.currency;
      }
      return { data };
    }

    return { data: payload };
  }

  /**
   * null = payload não trouxe items (usar state conforme calculateValueFrom)
   * [] = payload trouxe items vazios
   */
  private readPayloadItems(
    input: CommerceResolverInput
  ): ResolvedCommerceItem[] | null {
    if (input.items) {
      return input.items.map(item => this.normalizeLooseItem(item));
    }

    const raw = input.payload?.items;
    if (raw === undefined) return null;
    if (!Array.isArray(raw)) return null;
    return raw.map(item =>
      this.normalizeLooseItem(
        (item && typeof item === 'object' ? item : {}) as Record<
          string,
          unknown
        >
      )
    );
  }

  private resolveItemsSource(
    manualItems: ResolvedCommerceItem[] | null,
    enrichmentSource?: import('@/config/shared/commerce').CommerceEnrichmentSource
  ): ResolvedCommerceItem[] {
    // Items manuais sempre vencem state.
    if (manualItems !== null) {
      return manualItems.map(item => this.enrichItem(item));
    }

    // source: 'catalog' — não injeta cart inteiro
    if (enrichmentSource === 'catalog') {
      return [];
    }

    // source: 'cart' força fallback do carrinho (independente de calculateValueFrom),
    // exceto quando calculateValueFrom === 'manual' (value/items só do payload).
    if (
      enrichmentSource === 'cart' &&
      this.calculateValueFrom !== 'manual' &&
      this.calculateValueFrom !== 'event'
    ) {
      return this.state.getSelectedItems().map(stateItem =>
        this.enrichItem({
          productId: stateItem.productId,
          price: stateItem.price,
          quantity: stateItem.quantity,
          currency: stateItem.currency,
        })
      );
    }

    if (this.calculateValueFrom === 'cart') {
      return this.state.getSelectedItems().map(stateItem =>
        this.enrichItem({
          productId: stateItem.productId,
          price: stateItem.price,
          quantity: stateItem.quantity,
          currency: stateItem.currency,
        })
      );
    }

    if (this.calculateValueFrom === 'catalog') {
      const fromState = this.state.getSelectedItems();
      if (fromState.length > 0) {
        return fromState.map(stateItem =>
          this.enrichItem({
            productId: stateItem.productId,
            price: stateItem.price,
            quantity: stateItem.quantity,
            currency: stateItem.currency,
          })
        );
      }
      return this.catalog.list().map(product =>
        this.enrichItem({
          productId: product.id,
          price: product.price,
          quantity: product.quantity ?? 1,
          currency: product.currency,
          productName: product.name,
          sku: product.sku,
          category: product.category,
        })
      );
    }

    // event / manual sem items no payload → sem items
    return [];
  }

  private enrichItem(item: ResolvedCommerceItem): ResolvedCommerceItem {
    const productId =
      item.productId ?? this.lookupIdBySku(item.sku) ?? undefined;
    const catalog = productId
      ? this.catalog.getById(productId)
      : this.lookupProductBySku(item.sku);

    const resolvedId = productId ?? catalog?.id;

    return {
      ...item,
      ...(resolvedId ? { productId: resolvedId } : {}),
      productName: item.productName ?? catalog?.name,
      sku: item.sku ?? catalog?.sku,
      price: item.price ?? catalog?.price,
      quantity: item.quantity ?? catalog?.quantity ?? 1,
      currency: item.currency ?? catalog?.currency ?? this.defaultCurrency,
      category: item.category ?? catalog?.category,
      variant: item.variant ?? catalog?.variant,
      brand: item.brand ?? catalog?.brand,
      type: item.type ?? catalog?.type,
      metadata: item.metadata ?? catalog?.metadata,
    };
  }

  private toPixelItem(item: ResolvedCommerceItem): PixelItem {
    const pixel: PixelItem = {
      ...(item.productId ? { id: item.productId } : {}),
      ...(item.productName ? { name: item.productName } : {}),
      ...(item.category ? { category: item.category } : {}),
      ...(item.price !== undefined ? { price: item.price } : {}),
      ...(item.quantity !== undefined ? { quantity: item.quantity } : {}),
    };
    return pixel;
  }

  private normalizeLooseItem(
    item:
      | Record<string, unknown>
      | {
          productId?: string;
          id?: string;
          sku?: string;
          name?: string;
          productName?: string;
          price?: number;
          quantity?: number;
          currency?: string;
          category?: string;
        }
  ): ResolvedCommerceItem {
    return {
      productId:
        asString((item as { productId?: unknown }).productId) ??
        asString((item as { id?: unknown }).id),
      sku: asString((item as { sku?: unknown }).sku),
      productName:
        asString((item as { productName?: unknown }).productName) ??
        asString((item as { name?: unknown }).name),
      price: asNumber((item as { price?: unknown }).price),
      quantity: asNumber((item as { quantity?: unknown }).quantity),
      currency: asString((item as { currency?: unknown }).currency),
      category: asString((item as { category?: unknown }).category),
    };
  }

  private lookupIdBySku(sku: string | undefined): string | undefined {
    if (!sku) return undefined;
    return this.catalog.getBySku(sku)?.id;
  }

  private lookupProductBySku(sku: string | undefined) {
    if (!sku) return null;
    return this.catalog.getBySku(sku);
  }
}

function resolveCatalogItemIdentity(
  catalogProduct: import('@/config/shared/commerce').CommerceProduct | null,
  incoming: {
    id?: string;
    priceId?: string;
    productId?: string;
    offerId?: string;
  }
): {
  id?: string;
  priceId?: string;
  productId?: string;
  offerId?: string;
} {
  const meta = catalogProduct?.metadata ?? {};
  const metaPriceId = asString(meta.priceId);
  const metaProductId = asString(meta.productId);
  const metaOfferId = asString(meta.offerId);

  // Identidade Bemony (id/priceId/offerId) só quando priceId explícito ou metadata.
  // Catálogo genérico do Core continua só com productId.
  const priceId = incoming.priceId ?? metaPriceId;
  if (!priceId) {
    const productId = incoming.productId ?? catalogProduct?.id;
    return {
      ...(incoming.id ? { id: incoming.id } : {}),
      ...(productId ? { productId } : {}),
      ...(incoming.offerId ? { offerId: incoming.offerId } : {}),
    };
  }

  return {
    id: incoming.id ?? priceId,
    priceId,
    productId: metaProductId ?? incoming.productId ?? priceId,
    offerId: metaOfferId ?? incoming.offerId ?? priceId,
  };
}

function asString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function asNumber(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  return value;
}

function trim(value: string | undefined): string | undefined {
  if (typeof value !== 'string') return undefined;
  const t = value.trim();
  return t.length > 0 ? t : undefined;
}
